import type { ActionParamMetadataOutDTO } from '@api/ringil-api.v2';
import { isMoneyValue, isRecordValue, MoneyValue, RecordValue, RuleValue } from '../../ruleAst';

/**
 * "Price" for an action that takes a fixed `price` (MONEY) and/or a computed `price_tariff`
 * (TARIFF) — `assign_carrier`, `distribute_to_carriers`, `distribute_to_best_carriers`.
 *
 * A tariff is a list of parts that add up. Each part measures one quantity (`by`): the distance
 * (`km`), or the count of one or more handling-unit codes. `unit_price` multiplies that quantity
 * by a rate; `range_price` takes the flat price of the band the quantity falls into, `from`
 * inclusive and `to` exclusive, only the last band open-ended. The given `price` is the fallback
 * when the tariff does not price an expedition.
 *
 * Detected from the parameter shape, never from the action's key, so any future action declaring
 * the same pair gets the same section.
 */
export type PriceMode = 'NONE' | 'FIXED' | 'TARIFF';

export const PRICE = 'price';
export const PRICE_TARIFF = 'price_tariff';

/** The parameters the section owns; the builder renders them itself rather than as plain fields. */
export const PRICE_MODE_PARAMS = [PRICE, PRICE_TARIFF];

/** The distance basis. Anything else in `by` is a handling-unit code. */
export const KM = 'km';

/** The picker list of handling-unit codes, in `entityRefOptions` — not a type the metadata names. */
export const HANDLING_UNIT_REF_TYPE = 'HANDLING_UNIT';

type ParamLike = Pick<ActionParamMetadataOutDTO, 'name' | 'paramType'>;

export const hasPriceMode = (params: ParamLike[] | undefined): boolean => {
  const byName = (name: string) => (params ?? []).find(param => param.name === name);
  return String(byName(PRICE)?.paramType) === 'MONEY' && String(byName(PRICE_TARIFF)?.paramType) === 'TARIFF';
};

/**
 * Parameter groups of which at least one must be given, per action. The backend declares them
 * (`requiredOneOf` on the action handler) and refuses a rule without one on save
 * (`action_needs_one_of`), but `/metadata` does not carry them yet — both parameters arrive as
 * optional. Delete an entry once the metadata says it.
 */
const REQUIRED_ONE_OF: Record<string, string[][]> = {
  assign_carrier: [[PRICE, PRICE_TARIFF]],
};

/** Whether the action cannot run without a price — then "none" is not offered. */
export const priceRequired = (action: string | undefined): boolean =>
  (REQUIRED_ONE_OF[action ?? ''] ?? []).some(group => group.includes(PRICE) && group.includes(PRICE_TARIFF));

const isSet = (value: RuleValue | undefined) => value !== null && value !== undefined;

/** A copy without the named keys. */
const omit = <T extends object>(object: T, ...names: string[]): T => Object.fromEntries(Object.entries(object).filter(([name]) => !names.includes(name))) as T;

/**
 * Which choice the stored parameters express. A tariff wins over a price, since with a tariff the
 * price is only its fallback. With neither, the caller's remembered choice stands — so picking
 * "Tariff" on an empty action does not snap back — except that a required price never reads "none".
 */
export const priceModeOf = (params: Record<string, RuleValue> | undefined, fallback: PriceMode, required = false): PriceMode => {
  if (isSet(params?.[PRICE_TARIFF])) return 'TARIFF';
  if (isSet(params?.[PRICE])) return 'FIXED';
  return required && fallback === 'NONE' ? 'FIXED' : fallback;
};

/**
 * The parameters after switching to `mode`. Keys are dropped, never nulled — a null prints as
 * `price: null`. A fixed price survives the switch to a tariff, where it becomes the fallback.
 */
export const withPriceMode = (params: Record<string, RuleValue> | undefined, mode: PriceMode): Record<string, RuleValue> => {
  const { [PRICE]: price, [PRICE_TARIFF]: tariff, ...rest } = params ?? {};
  switch (mode) {
    case 'NONE':
      return rest;
    case 'FIXED':
      return isSet(price) ? { ...rest, [PRICE]: price } : rest;
    case 'TARIFF':
      return { ...rest, ...(isSet(price) ? { [PRICE]: price } : {}), [PRICE_TARIFF]: isSet(tariff) ? tariff : [emptyPart()] };
  }
};

/** The parameters with one set, or removed when emptied — never left as a null. */
export const withParam = (params: Record<string, RuleValue>, name: string, value: RuleValue | undefined): Record<string, RuleValue> => {
  return isSet(value) ? { ...params, [name]: value } : omit(params, name);
};

// ---------------------------------------------------------------------------
// Tariff parts
// ---------------------------------------------------------------------------

export type PartMode = 'unit_price' | 'range_price';

/**
 * Fields not filled in yet are absent rather than null — a null prints as `price: null` — so the
 * validator says what is missing (`tariff_needs_price`) instead of failing to parse.
 */
export interface Band extends RecordValue {
  kind: 'band';
  from?: number;
  to?: number;
  price?: MoneyValue;
}

export interface TariffPart extends RecordValue {
  kind: PartMode;
  by?: string | string[];
  price?: MoneyValue;
  bands?: Band[];
}

/**
 * The order each record's fields are written in. The AST keeps fields in insertion order and the
 * printer follows it, so a `to` filled in after the price would print `band(from: 0, price: …, to: 10)`.
 */
const FIELD_ORDER: Record<string, string[]> = {
  unit_price: ['kind', 'by', 'price'],
  range_price: ['kind', 'by', 'bands'],
  band: ['kind', 'from', 'to', 'price'],
};

const inFieldOrder = <T extends RecordValue>(record: T): T => {
  const order = FIELD_ORDER[record.kind] ?? [];
  const known = order.filter(name => name in record).map(name => [name, record[name]]);
  const unknown = Object.entries(record).filter(([name]) => !order.includes(name));
  return Object.fromEntries([...known, ...unknown]) as T;
};

/** The record with one field set, or removed when the value is empty. Fields stay in their written order. */
export const withField = <T extends RecordValue>(record: T, name: string, value: RuleValue | undefined): T => {
  return isSet(value) ? inFieldOrder({ ...record, [name]: value }) : omit(record, name);
};

/** The tariff's parts as stored, anything that is not a part left out. */
export const tariffParts = (value: RuleValue | undefined): TariffPart[] =>
  (Array.isArray(value) ? value : []).filter((part): part is TariffPart => isRecordValue(part) && (part.kind === 'unit_price' || part.kind === 'range_price'));

/** A new part: per km, which is the commonest tariff. */
export const emptyPart = (): TariffPart => ({ kind: 'unit_price', by: KM });

/** `by` as a list, however it was written — `km`, `EP` or `[EP, IBC]`. */
export const basisCodes = (by: RuleValue | undefined): string[] =>
  Array.isArray(by) ? by.filter((code): code is string => typeof code === 'string') : typeof by === 'string' && by ? [by] : [];

/** One code is written bare (`by: km`, `by: EP`), several as a list, as an author would type them. */
const basisValue = (codes: string[]): string | string[] | undefined => (codes.length === 0 ? undefined : codes.length === 1 ? codes[0] : codes);

/**
 * The basis after the picker's selection changed. `km` cannot share a part with handling units
 * (`tariff_km_alone`), so picking it clears the units and picking a unit clears `km`.
 */
export const withBasis = (part: TariffPart, selected: string[]): TariffPart => {
  const previous = basisCodes(part.by);
  const addedKm = selected.includes(KM) && !previous.includes(KM);
  const codes = addedKm ? [KM] : selected.length > 1 ? selected.filter(code => code !== KM) : selected;
  return withField(part, 'by', basisValue(codes));
};

/**
 * The part after switching between per unit and ranges. The basis stays; a per-unit rate and a
 * band list share nothing, so the other mode's field is dropped. A range starts with one open band
 * from 0, the shape of "this price whatever the quantity".
 */
export const withPartMode = (part: TariffPart, mode: PartMode): TariffPart => {
  if (part.kind === mode) return part;
  const rest = omit(part, 'price', 'bands');
  return mode === 'unit_price' ? { ...rest, kind: mode } : withField({ ...rest, kind: mode }, 'bands', [{ kind: 'band', from: 0 }]);
};

/**
 * A band appended after the last one. It starts where the last one ends, so bands always touch.
 * When the last band was the open one it has no end to start from: both bounds are left for the
 * author, and the validator says the open band is no longer last until they are filled.
 */
export const withBandAdded = (bands: Band[]): Band[] => {
  const last = bands[bands.length - 1];
  return [...bands, withField<Band>({ kind: 'band' }, 'from', last ? last.to : 0)];
};

/**
 * One band's `to` changed. The next band's `from` follows it, so the bands keep touching while the
 * author edits a bound. An emptied `to` is dropped — the band becomes the open one.
 */
export const withBandTo = (bands: Band[], index: number, to: number | null): Band[] =>
  bands.map((band, i) => {
    if (i === index) return withField(band, 'to', to);
    if (i === index + 1 && to !== null && to !== undefined) return withField(band, 'from', to);
    return band;
  });

/** One band's `from` changed. The previous band's `to` follows it, for the same reason. */
export const withBandFrom = (bands: Band[], index: number, from: number | null): Band[] =>
  bands.map((band, i) => {
    if (i === index) return withField(band, 'from', from);
    if (i === index - 1 && from !== null && from !== undefined) return withField(band, 'to', from);
    return band;
  });

/**
 * A band removed. Its neighbours are joined again: the next band starts where the removed one
 * did; removing the last band makes its predecessor the open one.
 */
export const withBandRemoved = (bands: Band[], index: number): Band[] => {
  const removed = bands[index];
  const remaining = bands.filter((_, i) => i !== index);
  if (index < remaining.length) return remaining.map((band, i) => (i === index ? withField(band, 'from', removed.from) : band));
  return remaining.map((band, i) => (i === remaining.length - 1 ? withField(band, 'to', undefined) : band));
};

/**
 * Every price in a tariff, and its fallback, share one currency (`tariff_mixed_currencies`), so a
 * currency picked on any of them is applied to all — the author cannot produce a mixed tariff.
 */
export const withTariffCurrency = (params: Record<string, RuleValue>, currency: string): Record<string, RuleValue> => {
  const recurrency = <T extends RecordValue>(record: T): T => (isMoneyValue(record.price) ? { ...record, price: { ...record.price, currency } } : record);
  const parts = tariffParts(params[PRICE_TARIFF]).map(part => (part.kind === 'unit_price' ? recurrency(part) : { ...part, bands: (part.bands ?? []).map(recurrency) }));
  const price = params[PRICE];
  // Only what is there is touched: an empty `price_tariff: []` would read as "Tariff" and snap the
  // mode back — the currency picker reports its default on mount, before anything is entered.
  return {
    ...params,
    ...(PRICE_TARIFF in params ? { [PRICE_TARIFF]: parts } : {}),
    ...(isMoneyValue(price) ? { [PRICE]: { ...price, currency } } : {}),
  };
};

interface Text {
  key: string;
  text: string;
}

/** What each choice does at run time — said once under the switch, since "none" is easy to misread. */
export const priceModeHint = (mode: PriceMode): Text => {
  switch (mode) {
    case 'NONE':
      return { key: 'AiDispatcher.priceModeHint.none', text: 'The demand asks for no price — carriers quote their own.' };
    case 'FIXED':
      return { key: 'AiDispatcher.priceModeHint.fixed', text: 'Always this price.' };
    case 'TARIFF':
      return {
        key: 'AiDispatcher.priceModeHint.tariff',
        text: 'Computed from the expedition: the parts are added up. When the tariff does not price it, the fallback applies.',
      };
  }
};

/** The currency the tariff is in: the first one written, for prefilling the next price. */
export const tariffCurrency = (params: Record<string, RuleValue>): string | undefined => {
  const prices = tariffParts(params[PRICE_TARIFF]).flatMap(part => (part.kind === 'unit_price' ? [part.price] : (part.bands ?? []).map(band => band.price)));
  return [...prices, params[PRICE] as MoneyValue].find(price => isMoneyValue(price) && price.currency)?.currency;
};
